import type { MoneyFragment, NonSkuLabGrownFragment } from '@/graphql/api'
import {
    normalizeUnifiedPdpClarityCode,
    normalizeUnifiedPdpColourCode,
} from '@/components/rings/engagement/unified-pdp/stoneFilterFormatting.ts'
import type { UnifiedPdpFilters } from '@/views/product/engagement-ring/composables/useEngagementRingUnifiedPdp.ts'
import { pickClosestCarat } from '@/domain/product/ring/naturalDiamondPreset.ts'

/** Marketing export carats from monolith LabGrownPricingCellRepository. */
export const unifiedPdpMarketingCarats = [
    0.7, 0.9, 1.0, 1.5, 2.5, 4.0, 7.0,
] as const

export const unifiedPdpDefaultColour = 'F'
export const unifiedPdpDefaultClarity = 'VS1'

/**
 * Prototype fallback stone GBP amounts when API cells are unavailable.
 * PLP uses shape-only lookup; PDP uses as last resort when matrix misses a cell.
 */
export const unifiedPdpPrototypeStonePriceGbpByShape: Record<string, number> = {
    ROUND: 640,
    ASSCHER: 705,
    HEXAGONAL: 980,
}

export const unifiedPdpPrototypeDefaultStonePriceGbp = 670

export type UnifiedPdpLabGrownCell = NonSkuLabGrownFragment

export interface UnifiedPdpLabGrownCellKey {
    carat: number
    colour: string
    clarity: string
}

const normalizeCarat = (carat: number | string): number =>
    Math.round(parseFloat(String(carat)) * 100) / 100

const caratsEqual = (a: number, b: number): boolean =>
    Math.abs(normalizeCarat(a) - normalizeCarat(b)) < 0.001

export const getCellKey = (
    cell: UnifiedPdpLabGrownCell
): UnifiedPdpLabGrownCellKey => ({
    carat: normalizeCarat(cell.caratWeight),
    colour: normalizeUnifiedPdpColourCode(cell.color.systemCode),
    clarity: normalizeUnifiedPdpClarityCode(
        cell.clarity.abbreviation || cell.clarity.systemCode
    ),
})

export const filtersToCellKey = (
    filters: UnifiedPdpFilters
): UnifiedPdpLabGrownCellKey => ({
    carat: normalizeCarat(filters.carat),
    colour: normalizeUnifiedPdpColourCode(filters.colour),
    clarity: normalizeUnifiedPdpClarityCode(filters.clarity),
})

export const getDefaultCaratTargetForShape = (shapeCode: string): number => {
    const normalized = shapeCode.toUpperCase()

    if (normalized === 'ASSCHER' || normalized === 'HEXAGONAL') {
        return 1.0
    }

    return 0.9
}

export const getPrototypeDefaultStonePriceGbp = (shapeCode: string): number => {
    const normalized = shapeCode.toUpperCase()

    return (
        unifiedPdpPrototypeStonePriceGbpByShape[normalized] ??
        unifiedPdpPrototypeDefaultStonePriceGbp
    )
}

export const parseMoneyAmount = (
    money: MoneyFragment | null | undefined
): number => {
    if (!money?.amount) {
        return 0
    }

    return parseFloat(money.amount)
}

export const addMoneyAmounts = (
    mount: MoneyFragment,
    stone: MoneyFragment,
    currency: MoneyFragment['currency'] = mount.currency
): MoneyFragment => ({
    __typename: 'Money',
    amount: (parseMoneyAmount(mount) + parseMoneyAmount(stone)).toFixed(2),
    currency,
})

export const buildCellLookup = (
    cells: UnifiedPdpLabGrownCell[]
): Map<string, UnifiedPdpLabGrownCell> => {
    const lookup = new Map<string, UnifiedPdpLabGrownCell>()

    for (const cell of cells) {
        const key = getCellKey(cell)
        lookup.set(`${key.carat}|${key.colour}|${key.clarity}`, cell)
    }

    return lookup
}

export const resolveCellFromLookup = (
    lookup: Map<string, UnifiedPdpLabGrownCell>,
    filters: UnifiedPdpFilters
): UnifiedPdpLabGrownCell | null => {
    const key = filtersToCellKey(filters)

    return lookup.get(`${key.carat}|${key.colour}|${key.clarity}`) ?? null
}

const getAvailableCarats = (cells: UnifiedPdpLabGrownCell[]): number[] => {
    const carats = new Set<number>()

    for (const cell of cells) {
        carats.add(normalizeCarat(cell.caratWeight))
    }

    return [...carats].sort((a, b) => a - b)
}

export const pickClosestCaratAtOrAbove = (
    targetCarat: number,
    availableCarats: number[]
): number | null => {
    const sorted = [...availableCarats].sort((a, b) => a - b)
    const match = sorted.find((carat) => carat >= targetCarat - 0.001)

    return match ?? sorted[sorted.length - 1] ?? null
}

/**
 * Cell at the carat closest to `filters.carat`, keeping its grades when that
 * carat has them, otherwise the default grades.
 */
export const resolveClosestCaratCell = (
    cells: UnifiedPdpLabGrownCell[],
    filters: UnifiedPdpFilters
): UnifiedPdpLabGrownCell | null => {
    const carat = pickClosestCarat(
        normalizeCarat(filters.carat),
        getAvailableCarats(cells)
    )

    if (carat === null) {
        return null
    }

    const lookup = buildCellLookup(cells)

    return (
        resolveCellFromLookup(lookup, { ...filters, carat }) ??
        resolveCellFromLookup(lookup, {
            carat,
            colour: unifiedPdpDefaultColour,
            clarity: unifiedPdpDefaultClarity,
        }) ??
        cells.find((cell) =>
            caratsEqual(normalizeCarat(cell.caratWeight), carat)
        ) ??
        null
    )
}

export const pickDefaultLabGrownCell = (
    cells: UnifiedPdpLabGrownCell[],
    shapeCode: string
): UnifiedPdpLabGrownCell | null => {
    const lookup = buildCellLookup(cells)
    const targetCarat = getDefaultCaratTargetForShape(shapeCode)
    const availableCarats = getAvailableCarats(cells)
    const carat = pickClosestCaratAtOrAbove(targetCarat, availableCarats)

    if (carat === null) {
        return null
    }

    const defaultCell = resolveCellFromLookup(lookup, {
        carat,
        colour: unifiedPdpDefaultColour,
        clarity: unifiedPdpDefaultClarity,
    })

    if (defaultCell) {
        return defaultCell
    }

    const sameCaratCell = cells.find((cell) =>
        caratsEqual(normalizeCarat(cell.caratWeight), carat)
    )

    return sameCaratCell ?? cells[0] ?? null
}

export const pickNextUpgradeCarat = (
    currentCarat: number,
    availableCarats: number[]
): number | null => {
    const normalizedCurrent = normalizeCarat(currentCarat)
    const sorted = [...availableCarats].sort((a, b) => a - b)
    const next = sorted.find((carat) => carat > normalizedCurrent + 0.001)

    return next ?? null
}

export const pickUpgradeLabGrownCell = (
    cells: UnifiedPdpLabGrownCell[],
    currentFilters: UnifiedPdpFilters
): UnifiedPdpLabGrownCell | null => {
    const lookup = buildCellLookup(cells)
    const availableCarats = getAvailableCarats(cells)
    const nextCarat = pickNextUpgradeCarat(
        currentFilters.carat,
        availableCarats
    )

    if (nextCarat === null) {
        return null
    }

    return resolveCellFromLookup(lookup, {
        carat: nextCarat,
        colour: currentFilters.colour,
        clarity: currentFilters.clarity,
    })
}

export type UnifiedPdpPriceDeltaAxis = 'carat' | 'colour' | 'clarity'

export const buildPriceDeltaMap = (
    cells: UnifiedPdpLabGrownCell[],
    selectedFilters: UnifiedPdpFilters,
    axis: UnifiedPdpPriceDeltaAxis
): Record<string, number> => {
    const lookup = buildCellLookup(cells)
    const selectedCell = resolveCellFromLookup(lookup, selectedFilters)

    if (!selectedCell) {
        return {}
    }

    const selectedAmount = parseMoneyAmount(selectedCell.salePrice)
    const deltas: Record<string, number> = {}

    const options =
        axis === 'carat'
            ? getAvailableCarats(cells).map(String)
            : axis === 'colour'
              ? [
                    ...new Set(
                        cells.map((cell) =>
                            normalizeUnifiedPdpColourCode(cell.color.systemCode)
                        )
                    ),
                ].sort()
              : [
                    ...new Set(
                        cells.map((cell) =>
                            normalizeUnifiedPdpClarityCode(
                                cell.clarity.abbreviation ||
                                    cell.clarity.systemCode
                            )
                        )
                    ),
                ].sort()

    for (const option of options) {
        const candidateFilters: UnifiedPdpFilters = { ...selectedFilters }

        if (axis === 'carat') {
            candidateFilters.carat = normalizeCarat(parseFloat(option))
        } else if (axis === 'colour') {
            candidateFilters.colour = option
        } else {
            candidateFilters.clarity = option
        }

        const candidateCell = resolveCellFromLookup(lookup, candidateFilters)

        if (!candidateCell) {
            continue
        }

        deltas[option] =
            parseMoneyAmount(candidateCell.salePrice) - selectedAmount
    }

    return deltas
}

export const getFilterBoundsFromCells = (
    cells: UnifiedPdpLabGrownCell[]
): {
    caratQuickSelect: number[]
    caratSliderMin: number
    caratSliderMax: number
    colours: string[]
    clarities: string[]
} => {
    const carats = getAvailableCarats(cells)
    const colours = [
        ...new Set(
            cells.map((cell) =>
                normalizeUnifiedPdpColourCode(cell.color.systemCode)
            )
        ),
    ].sort()
    const clarities = [
        ...new Set(
            cells.map((cell) =>
                normalizeUnifiedPdpClarityCode(
                    cell.clarity.abbreviation || cell.clarity.systemCode
                )
            )
        ),
    ].sort()

    return {
        caratQuickSelect:
            carats.length > 0 ? carats : [...unifiedPdpMarketingCarats],
        caratSliderMin: carats[0] ?? 0.5,
        caratSliderMax: carats[carats.length - 1] ?? 15,
        colours: colours.length > 0 ? colours : ['D', 'F'],
        clarities: clarities.length > 0 ? clarities : ['VVS2', 'VS1'],
    }
}

export const getRelativeStonePriceDelta = (
    selectedCell: UnifiedPdpLabGrownCell | null,
    candidateCell: UnifiedPdpLabGrownCell | null
): number | null => {
    if (!selectedCell || !candidateCell) {
        return null
    }

    const delta =
        parseMoneyAmount(candidateCell.salePrice) -
        parseMoneyAmount(selectedCell.salePrice)

    if (Math.abs(delta) < 0.005) {
        return null
    }

    return delta
}

export const createPrototypeFallbackCell = (
    shapeCode: string,
    filters: UnifiedPdpFilters,
    currency: MoneyFragment['currency']
): UnifiedPdpLabGrownCell => {
    const amount = getPrototypeDefaultStonePriceGbp(shapeCode).toFixed(2)

    return {
        __typename: 'NonSkuLabGrown',
        id: `prototype-${shapeCode}-${filters.carat}-${filters.colour}-${filters.clarity}`,
        caratWeight: String(filters.carat),
        salePrice: {
            __typename: 'Money',
            amount,
            currency,
        },
        shape: {
            __typename: 'StoneShape',
            systemCode: shapeCode,
            icon: null,
            samplePhoto: null,
            translation: {
                __typename: 'StoneShapeTranslation',
                name: shapeCode,
            },
        },
        color: {
            __typename: 'StoneColor',
            systemCode: filters.colour,
            translation: {
                __typename: 'StoneColorTranslation',
                name: filters.colour,
            },
        },
        clarity: {
            __typename: 'StoneClarity',
            systemCode: filters.clarity,
            abbreviation: filters.clarity,
            translation: {
                __typename: 'StoneClarityTranslation',
                name: filters.clarity,
            },
        },
        image: null,
        shapeDimension: null,
    } as unknown as UnifiedPdpLabGrownCell
}

/**
 * Exact cell for the filters, or the closest existing combination. The axis
 * the user just changed is kept whenever a cell allows it.
 */
export const resolveClosestCell = (
    cells: UnifiedPdpLabGrownCell[],
    filters: UnifiedPdpFilters,
    priority: UnifiedPdpPriceDeltaAxis = 'carat'
): UnifiedPdpLabGrownCell | null => {
    const exact = resolveCellFromLookup(buildCellLookup(cells), filters)

    if (exact) {
        return exact
    }

    const target = filtersToCellKey(filters)

    const score = (cell: UnifiedPdpLabGrownCell): number => {
        const key = getCellKey(cell)
        const matches: Record<UnifiedPdpPriceDeltaAxis, boolean> = {
            carat: caratsEqual(key.carat, target.carat),
            colour: key.colour === target.colour,
            clarity: key.clarity === target.clarity,
        }

        return (
            (matches[priority] ? 8 : 0) +
            (matches.carat ? 4 : 0) +
            (matches.colour ? 2 : 0) +
            (matches.clarity ? 2 : 0) -
            Math.abs(key.carat - target.carat) / 100
        )
    }

    let closest: UnifiedPdpLabGrownCell | null = null

    for (const cell of cells) {
        if (!closest || score(cell) > score(closest)) {
            closest = cell
        }
    }

    return closest
}
